import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/feature/MovieCard';
import { movies, categories } from '@/mocks/movies';

function ArticleSchema(movie: typeof movies[0]) {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Movie',
    name: movie.title,
    description: movie.description,
    image: movie.coverImage,
    dateCreated: `${movie.year}`,
    director: { '@type': 'Person', name: movie.director },
    actor: movie.cast.map((name) => ({ '@type': 'Person', name })),
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: movie.rating,
      bestRating: '10',
      ratingCount: Math.floor(Math.random() * 100000) + 10000,
    },
    genre: movie.subcategory,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const movieId = parseInt(id || '1', 10);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const movie = movies.find((m) => m.id === movieId);

  if (!movie) {
    return (
      <>
        <Navbar />
        <div className="pt-24 pb-20 text-center px-4">
          <h1 className="text-2xl font-heading font-bold text-foreground-900">影片未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 mt-4 inline-block cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const category = categories.find((c) => c.id === movie.category);
  const relatedMovies = movies
    .filter((m) => m.id !== movie.id && (m.category === movie.category || m.subcategory === movie.subcategory))
    .slice(0, 6);

  const breadcrumbItems = [
    { name: '首页', path: '/' },
    ...(category ? [{ name: category.name, path: `/category/${category.slug}` }] : []),
    { name: movie.title },
  ];

  return (
    <>
      {ArticleSchema(movie)}
      <Navbar />

      <main>
        <div className="pt-20 pb-6 px-4 md:px-6 mx-auto" style={{ maxWidth: '1400px' }}>
          <Breadcrumb items={breadcrumbItems} />
        </div>

        <article className="px-4 md:px-6 pb-16 mx-auto" style={{ maxWidth: '1400px' }}>
          <div className="flex flex-col lg:flex-row gap-8 md:gap-10">
            {/* Poster */}
            <div className="shrink-0 w-full sm:w-[280px] lg:w-[320px] mx-auto lg:mx-0">
              <div className="aspect-[2/3] rounded-xl overflow-hidden bg-background-200 shadow-lg">
                <img
                  src={movie.coverImage}
                  alt={movie.title}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="px-2.5 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium whitespace-nowrap">
                  {movie.subcategory}
                </span>
                {movie.isNew && (
                  <span className="px-2.5 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium whitespace-nowrap">
                    新上线
                  </span>
                )}
                {movie.featured && (
                  <span className="px-2.5 py-1 rounded-full bg-accent-500 text-white text-xs font-medium whitespace-nowrap">
                    精选推荐
                  </span>
                )}
              </div>

              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-3">
                {movie.title}
              </h1>

              <div className="flex flex-wrap items-center gap-3 md:gap-4 mb-5 text-sm">
                <span className="flex items-center gap-1.5 text-accent-500 font-semibold">
                  <i className="ri-star-fill"></i>
                  {movie.rating}
                  <span className="text-foreground-400 font-normal">/ 10</span>
                </span>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-500">{movie.year}</span>
                <span className="text-foreground-400">|</span>
                <span className="text-foreground-500">{movie.category === 'tv' || movie.category === 'variety' || movie.category === 'anime' || movie.category === 'documentary' ? movie.episodeCount : movie.duration}</span>
              </div>

              <p className="text-sm md:text-base text-foreground-600 leading-relaxed mb-6">
                {movie.description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8 text-sm">
                <div className="flex items-start gap-2">
                  <span className="text-foreground-400 shrink-0 whitespace-nowrap">导演：</span>
                  <span className="text-foreground-700">{movie.director}</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="text-foreground-400 shrink-0 whitespace-nowrap">主演：</span>
                  <span className="text-foreground-700">{movie.cast.join('、')}</span>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2 mb-8">
                {movie.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-lg bg-background-100 text-foreground-500 text-xs border border-background-200 whitespace-nowrap"
                  >
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <a
                  href="#play"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line text-lg"></i>
                  立即播放
                </a>
                <button className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-600 font-medium text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">
                  <i className="ri-heart-line"></i>
                  收藏
                </button>
              </div>
            </div>
          </div>

          {/* Related */}
          {relatedMovies.length > 0 && (
            <section className="mt-16">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-heading font-bold text-foreground-900">
                  相关推荐
                </h2>
                {category && (
                  <Link
                    to={`/category/${category.slug}`}
                    className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    更多{categories.find(c => c.id === movie.category)?.name} <i className="ri-arrow-right-line"></i>
                  </Link>
                )}
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4 md:gap-5">
                {relatedMovies.map((rm) => (
                  <MovieCard
                    key={rm.id}
                    id={rm.id}
                    title={rm.title}
                    coverImage={rm.coverImage}
                    rating={rm.rating}
                    year={rm.year}
                    subcategory={rm.subcategory}
                    category={rm.category}
                  />
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}